"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaServiceV2 = void 0;
const antlr4_1 = require("antlr4");
const sheet_db_1 = require("../sheet_db");
const utils_1 = require("../utils");
const formula_utils_1 = require("../utils/formula.utils");
const string_utils_1 = require("../utils/string.utils");
const antlr_files_1 = require("./antlr_files");
const listeners_1 = require("./antlr_files/listeners");
const custom_tree_1 = require("./custom_tree");
const visitors_1 = require("./custom_tree/visitors");
const visitors_2 = require("./visitors");
const bq_visitor_1 = require("./custom_tree/visitors/bq.visitor");
const formula_service_1 = require("./formula.service");
const tabs_context_1 = require("./context/tabs.context");
const map_utils_1 = require("../utils/map.utils");
class FormulaServiceV2 {
    outTranslator;
    inTranslator;
    tabsContext;
    big_query_dataset;
    big_query_project;
    constructor({ tabsContext, big_query_dataset = 'formulas', big_query_project = 'splitc-production', }) {
        this.outTranslator = sheet_db_1.OutFormulaTranslations;
        this.inTranslator = (0, formula_utils_1.getInTranslations)();
        this.tabsContext = new tabs_context_1.TabsContext(tabsContext);
        this.big_query_dataset = big_query_dataset;
        this.big_query_project = big_query_project;
    }
    updateSchema(tabs, locale = formula_service_1.DEFAULT_LOCALE) {
        const uniqueTabs = (0, utils_1.makeTabNamesUnique)(tabs);
        const oldTab = this.tabsContext.getCurrentTab();
        const newTab = uniqueTabs.find(t => oldTab.id === String(t.id));
        if (!newTab)
            throw new Error('Current tab not present in tabs');
        const uniqueNewSchema = (0, utils_1.makeColumnNamesUnique)(newTab.schema);
        const uniqueOldSchema = (0, utils_1.makeColumnNamesUnique)([...oldTab.columnsById.values()]);
        const oldSchemaMap = map_utils_1.MapUtils.arrayToMap(uniqueOldSchema, 'column_id');
        const newColumnNames = new Map();
        uniqueNewSchema.forEach(newCol => {
            const oldCol = oldSchemaMap.get(newCol.column_id);
            if (!oldCol)
                return;
            if (oldCol.name !== newCol.name)
                newColumnNames.set(oldCol.name, newCol.name);
        });
        const columnNameFixer = new visitors_1.ColumnNameFixVisitor(newColumnNames);
        uniqueNewSchema.forEach(newCol => {
            if (newCol.node_type !== sheet_db_1.NodeType.FORMULA && newCol.formula !== sheet_db_1.NodeType.WINDOW_FUNCTION)
                return;
            const { ast, errors } = this.getAst(newCol.formula ?? '');
            if (errors?.length)
                return;
            if (!ast)
                return;
            columnNameFixer.visit(ast);
            newCol.formula = ast.toString();
        });
        newTab.schema = uniqueNewSchema;
        return uniqueTabs;
    }
    splitAndValidateFormula(formulaRequest) {
        const { formula: formulaToTranslate, locale, field_name } = formulaRequest;
        if (formulaToTranslate === '') {
            return {
                columnName: field_name,
                formula: formulaToTranslate,
                columnType: sheet_db_1.ColumnType.STRING,
                errors: [],
            };
        }
        const { translation: formula, ast, errors, } = this.translateFormula({
            formula: formulaToTranslate,
            toResponse: false,
            locale,
        });
        if (!formula || !ast)
            return { columnName: field_name, formula, columnType: sheet_db_1.ColumnType.STRING, errors };
        const { returnType: columnType, errors: semanticErrors } = this.validateFormula(formula, ast);
        return {
            columnName: field_name,
            formula,
            columnType: columnType ?? sheet_db_1.ColumnType.STRING,
            errors: [...(errors ?? []), ...(semanticErrors ?? [])],
            isWindowFunction: ast.isWindowFunction(),
            treeNodeType: ast.root?.treeNodeType,
            args: ast.getFormulaArgs(),
        };
    }
    translateFormula({ formula, toResponse = true, locale, existentLexer, }) {
        const { ast, errors } = this.getAst(formula, existentLexer);
        if (errors?.length)
            return { errors };
        const chosenLocale = locale ? (0, string_utils_1.localeNormalizer)(locale) : null;
        const translations = chosenLocale
            ? (toResponse
                ? this.outTranslator[chosenLocale]
                : this.inTranslator[chosenLocale])
            : undefined;
        if (!ast)
            throw new Error('AST not found! AST nedded for translation process');
        const translation = ast.translate({ translations, isOut: toResponse });
        return { translation, ast, errors };
    }
    formatFormulaToExecute(formula) {
        const { ast, errors } = this.getAst(formula);
        if (errors?.length)
            return { errors, formattedFormula: formula };
        const translation = ast.translate({ isOut: false, formatToExecute: true });
        return { errors: [], formattedFormula: translation };
    }
    isWindowFunction(formula, locale = formula_service_1.DEFAULT_LOCALE) {
        const { ast } = this.translateFormula({
            formula,
            toResponse: false,
            locale,
        });
        if (ast?.root instanceof custom_tree_1.FormulaNode)
            return (0, sheet_db_1.isWindowFunctionCheck)(ast.root.formulaDef);
        return false;
    }
    getContextAtPosition(formula, position, lexer, locale = formula_service_1.DEFAULT_LOCALE) {
        const { ast } = this.translateFormula({
            formula,
            toResponse: false,
            existentLexer: lexer,
            locale,
        });
        return ast?.getContextAtPosition(position) ?? {};
    }
    getAst(formula, existentLexer) {
        let lexer = existentLexer;
        let lexerErrorHandler = undefined;
        if (!lexer) {
            const { lexer: createdLexer, lexerErrorHandler: createdLexerErrorHandler } = this.getLexer(formula);
            lexer = createdLexer;
            lexerErrorHandler = createdLexerErrorHandler;
        }
        const { parser, parserErrorHandler } = this.getParser(lexer);
        // concrete syntax tree
        const cst = parser.program();
        const errors = [];
        errors.push(...(lexerErrorHandler?.errors ?? []));
        errors.push(...(parserErrorHandler.errors ?? []));
        // this will remove intermediate nodes from our CST (but keeps concrete terminals like "(", ")", ",")
        const cstCleaner = new visitors_2.CstCleanupVisitor();
        const cleanedCst = cstCleaner.visitProgram(cst);
        // this will generate our custom AST
        const astGen = new visitors_2.AstGenVisitorv2(this.tabsContext);
        astGen.visit(cleanedCst);
        // finally our AST
        const ast = astGen.ast;
        return { ast, errors };
    }
    validateFormula(formula, receivedAst, lexer, locale = formula_service_1.DEFAULT_LOCALE) {
        let ast = receivedAst;
        const errors = [];
        if (!ast) {
            const r = this.translateFormula({
                formula,
                toResponse: false,
                existentLexer: lexer,
                locale,
            });
            ast = r.ast;
            if (r.errors)
                errors.push(...r.errors);
        }
        if (!ast)
            return { errors };
        const semanticVisitor = new visitors_1.SemanticVisitor();
        semanticVisitor.visit(ast);
        errors.push(...semanticVisitor.semanticErrors);
        return {
            returnType: ast.getReturnType(),
            errors: errors.map(e => {
                if (e.to >= formula.length)
                    e.to = formula.length - 1;
                if (e.from >= formula.length)
                    e.from = formula.length - 1;
                return e;
            }),
        };
    }
    getParser(lexer) {
        const tokens = new antlr4_1.CommonTokenStream(lexer);
        const parserErrorHandler = new listeners_1.ParserErrors();
        const parser = new antlr_files_1.Parser(tokens);
        parser.addErrorListener(parserErrorHandler);
        return { parser, parserErrorHandler };
    }
    getLexer(formula) {
        const chars = antlr4_1.CharStreams.fromString(formula);
        const lexerErrorHandler = new listeners_1.LexerErrors();
        const lexer = new antlr_files_1.Lexer(chars);
        lexer.addErrorListener(lexerErrorHandler);
        return { lexer, lexerErrorHandler };
    }
    getReferencedColumns = (formula) => {
        if (formula === '') {
            return {
                referencedColumns: [],
                planColumns: [],
            };
        }
        const { ast } = this.getAst(formula);
        if (!ast)
            throw new Error('AST not found');
        ast.translate({ isOut: true });
        const referencedColumnsVisitor = new visitors_1.RefColVisitor();
        referencedColumnsVisitor.visit(ast);
        return {
            referencedColumns: referencedColumnsVisitor.referencedColumns,
            planColumns: referencedColumnsVisitor.planColumns,
        };
    };
    getBigQueryFormula({ metadata, formula }) {
        const { ast } = this.getAst(formula);
        // FIXME: after making changes in V1, handle that change in V2
        const bqVisitor = new bq_visitor_1.BqVisitor(ast, this.big_query_project, this.big_query_dataset, metadata);
        bqVisitor.visit(ast);
        return {
            formula: ast.toString(),
            args: ast.getFormulaArgs(),
        };
    }
}
exports.FormulaServiceV2 = FormulaServiceV2;
